import { Badge } from '@mantine/core';
import { MealPlan, Recipe } from '../types/mealPlan';

// A plan is past once its end date has passed. Past plans are frozen: a past
// draft is "expired", a past active plan is completed history — neither can
// be activated or modified any longer (the backend rejects those requests too).
export const isMealPlanPast = (plan: Pick<MealPlan, 'endDate'>): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];
  return plan.endDate < todayStr;
};

export const isMealPlanExpired = (
  plan: Pick<MealPlan, 'status' | 'endDate'>
): boolean => plan.status === 'draft' && isMealPlanPast(plan);

export const getStatusBadge = (plan: MealPlan) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  const generationStatus = plan.generationStatus.status;
  const isPastPlan = plan.endDate < todayStr;
  
  if (generationStatus === 'failed') {
    return <Badge color="red" variant="light" size="sm">Failed</Badge>;
  } else if (generationStatus === 'pending' || generationStatus === 'generating') {
    return <Badge color="blue" variant="light" size="sm">Creating</Badge>;
  } else if (generationStatus === 'completed') {
    if (isPastPlan && plan.status === 'active') {
      return <Badge color="blue" variant="light" size="sm">Completed</Badge>;
    } else if (isPastPlan && plan.status === 'draft') {
      return <Badge color="gray" variant="light" size="sm">Expired</Badge>;
    } else {
      // Show actual meal plan status for current/future plans
      if (plan.status === 'active') {
        return <Badge color="orange" variant="light" size="sm">Active</Badge>;
      } else {
        return <Badge color="indigo" variant="light" size="sm">Draft</Badge>;
      }
    }
  } else {
    return <Badge color="gray" variant="light" size="sm">{generationStatus}</Badge>;
  }
};

export const formatCreatedDate = (createdAt: string) => {
  if (!createdAt) return 'Unknown';

  const date = new Date(createdAt);

  if (isNaN(date.getTime())) return 'Unknown';

  const currentYear = new Date().getFullYear();
  const includeYear = date.getFullYear() !== currentYear;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(includeYear && { year: 'numeric' })
  });
};

export const sortMealPlansByStartDate = (plans: MealPlan[]) => {
  return plans.sort((a, b) => {
    // Sort by start date (soonest first)
    return a.startDate.localeCompare(b.startDate);
  });
};

// The plan-row meta line's meal count. Dinner-only plans keep the plain
// "N meals"; plans with breakfast/lunch count only the dinners (matching the
// dinner-scoped thumbnail strip beside it) and name the other meals instead
// of inflating the number with rotation repeats and reference rows.
export const formatPlanMealsSummary = (plan: MealPlan): string => {
  const additionalMeals = plan.preferences.additionalMeals;
  const total = plan.assignments?.length ?? 0;
  if (!additionalMeals?.breakfast && !additionalMeals?.lunch) {
    return `${total} meal${total !== 1 ? 's' : ''}`;
  }
  const dinners = (plan.assignments ?? []).filter(
    (a) => (a.mealType ?? 'dinner') === 'dinner'
  ).length;
  const others =
    additionalMeals.breakfast && additionalMeals.lunch
      ? 'breakfasts & lunches'
      : additionalMeals.breakfast
        ? 'breakfasts'
        : 'lunches';
  return `${dinners} dinner${dinners !== 1 ? 's' : ''}, plus ${others}`;
};

export const formatRecipeSource = (recipeSources: "ai_only" | "my_recipes_only" | "mixed") => {
  switch (recipeSources) {
    case 'ai_only':
      return 'New recipes only';
    case 'my_recipes_only':
      return 'My recipes only';
    case 'mixed':
      return 'Mixed recipes';
    default:
      return 'Unknown';
  }
};

export const formatDate = (dateString: string, long: boolean = false): string => {
  const date = new Date(dateString);
  const now = new Date();
  const includeYear = date.getFullYear() !== now.getFullYear();

  return date.toLocaleDateString('en-US', { 
      month: long ? 'long' : 'short', 
      day: 'numeric',
      ...(includeYear && { year: 'numeric' })
    })
}

export const formatDateRange = (plan: MealPlan, long: boolean = false): string => {
  // Parse dates manually to avoid timezone issues
  const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);
  const start = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);

  const format = (date: Date, opts: Intl.DateTimeFormatOptions = {}) =>
    date.toLocaleDateString('en-US', opts);

  // Same exact date
  if (start.getTime() === end.getTime()) {
    return format(start, {
      month: long ? 'long' : 'short',
      day: 'numeric',
    });
  }

  // Same month + year → only show month once
  if (startYear === endYear && startMonth === endMonth) {
    const monthOptions: Intl.DateTimeFormatOptions = {
      month: long ? 'long' : 'short',
    };
    const startDayStr = format(start, { day: 'numeric' });
    const endDayStr = format(end, { day: 'numeric' });

    return `${format(start, monthOptions)} ${startDayStr} - ${endDayStr}`;
  }

  // Different month or year
  const startFormatted = format(start, {
    month: long ? 'long' : 'short',
    day: 'numeric',
  });
  const endFormatted = format(end, {
    month: long ? 'long' : 'short',
    day: 'numeric',
  });

  return `${startFormatted} - ${endFormatted}`;
};

export const getExpectedDays = (
  plan: MealPlan,
): { dayName: string; plannedDate: string }[] => {
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-based
  }

  const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const startDate = parseLocalDate(plan.startDate);
  const endDate = parseLocalDate(plan.endDate);
  const result: { dayName: string; plannedDate: string }[] = [];

  for (
    let date = new Date(startDate.getTime());
    date <= endDate;
    date.setDate(date.getDate() + 1)
  ) {
    result.push({
      dayName: dayNames[date.getDay()],
      plannedDate: formatDate(date),
    });
  }

  return result;
}

export const isRecipeScaled = (recipe: Recipe): boolean => {
    return recipe.originalServings !== undefined && recipe.originalServings !== recipe.servings;
}
